import React from 'react'
import { css } from '@emotion/core'
import { Container, Box, Grid } from '@material-ui/core'
import { Formik } from 'formik'
import * as Yup from 'yup'

import { Theme } from '~/styles'
import { Heading, Typography, Button, Header, Main } from '~/components'
import Loader from '~/components/Loader/Loader'
import YellowChevron from '~/assets/icons/material/yellow_chevron.svg?react'

export interface SignupTypeOption {
  label: string
  value: string
  subtext: string
}

interface SignupTypeSelectionFormState {
  signupType: SignupTypeOption
}

interface SignupTypeSelectionUIProps {
  options?: SignupTypeOption[]
  onSelectOption: (option: SignupTypeOption) => void | Promise<void>
  onNoInsurance?: () => void
  isLoading?: boolean
  heading?: string
  showNoInsuranceButton?: boolean
}

const validationSchema = Yup.object().shape({
  signupType: Yup.object()
    .shape({
      label: Yup.string(),
      value: Yup.string().required(),
      subtext: Yup.string(),
    })
    .required('Please select an option'),
})

const initialState: SignupTypeSelectionFormState = {
  signupType: { label: '', value: '', subtext: '' },
}

/**
 * Reusable UI component for user type selection screens.
 * Options should be provided dynamically from the API.
 */
export const SignupTypeSelectionUI: React.FC<SignupTypeSelectionUIProps> = ({
  options = [],
  onSelectOption,
  onNoInsurance,
  isLoading = false,
  heading = "Welcome! Let's get you set up.",
  showNoInsuranceButton = true,
}) => {
  return isLoading ? (
    <>
      <Header />
      <Loader />
    </>
  ) : (
    <>
      <Header />
      <Container>
        <Box mt={2}>
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12} md={6}>
              <Grid container spacing={2} justifyContent="center">
                <Grid item xs={12} lg={10}>
                  <Main px={{ xs: 0 }}>
                    <Box mt={4} />
                    <Heading>{heading}</Heading>
                    <Box mt={4} />
                    <Formik<SignupTypeSelectionFormState>
                      validateOnMount={true}
                      validateOnChange={false}
                      initialValues={initialState}
                      onSubmit={values => {
                        if (values?.signupType) {
                          onSelectOption(values.signupType)
                        }
                      }}
                      validationSchema={validationSchema}
                    >
                      {formik => {
                        return (
                          <form onSubmit={formik.handleSubmit} css={styles.wrapper}>
                            <Box mb={2}>
                              {options.map(option => (
                                <Button
                                  key={option.value}
                                  title=""
                                  buttonColor="navy"
                                  css={styles.button}
                                  disabled={isLoading}
                                  type="button"
                                  onClick={() => {
                                    onSelectOption(option)
                                  }}
                                >
                                  <Box display="flex" flexDirection="row" alignItems="center">
                                    <Box flexGrow={1} css={styles.contentColumn}>
                                      <Typography css={styles.subText}>{option.subtext}</Typography>
                                      <Typography css={styles.stepText}>{option.label}</Typography>
                                    </Box>
                                    <YellowChevron css={styles.chevron} />
                                  </Box>
                                </Button>
                              ))}
                            </Box>

                            {showNoInsuranceButton && onNoInsurance && (
                              <Box mt={2} mb={6} display="flex" justifyContent="center">
                                <button
                                  css={styles.noInsuranceButton}
                                  onClick={onNoInsurance}
                                  type="button"
                                >
                                  I don&apos;t have health insurance
                                </button>
                              </Box>
                            )}
                          </form>
                        )
                      }}
                    </Formik>
                  </Main>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </>
  )
}

const styles = {
  wrapper: () => css({ width: '100%' }),
  button: (theme: Theme) =>
    css({
      borderRadius: '16px',
      marginBottom: '16px',
      width: '100%',
      paddingTop: '24px',
      paddingRight: '40px',
      paddingBottom: '20px',
      paddingLeft: '16px',
      backgroundColor: '#131D2B',
      border: 'none',
      minHeight: '120px',
      position: 'relative',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      transition: 'all 0.2s ease-in-out',
      ...{
        '&:hover': {
          backgroundColor: '#1a2332',
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
        },
        '&:focus': {
          backgroundColor: '#1a2332',
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
        },
      },
      [theme.mediaQueries.lg]: {
        width: '504px',
        paddingTop: '38px',
        paddingRight: '48px',
        paddingBottom: '24px',
        paddingLeft: '24px',
      },
    }),
  stepText: (theme: Theme) =>
    css({
      fontSize: '28px',
      fontWeight: 500,
      lineHeight: '36px',
      color: '#FEFFD6',
      marginTop: '8px',
      textAlign: 'left',
      whiteSpace: 'pre-line',
      fontFamily: 'Trust1A-Medium',
      letterSpacing: '-0.015em',
      [theme.mediaQueries.lg]: {
        fontSize: '42px',
        lineHeight: '62px',
        marginTop: '12px',
      },
    }),
  subText: (theme: Theme) =>
    css({
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: '22px',
      color: '#FEFFD6',
      textAlign: 'left',
      letterSpacing: 0,
      fontFamily: 'Roobert-Regular',
      marginTop: '0px',
      marginBottom: '4px',
      opacity: 0.8,
      [theme.mediaQueries.lg]: {
        fontSize: '18px',
        lineHeight: '25px',
      },
    }),
  contentColumn: () =>
    css({
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    }),
  noInsuranceButton: (theme: Theme) =>
    css({
      background: 'transparent',
      border: 'none',
      padding: 0,
      color: theme.colors.blue[700],
      fontSize: '16px',
      fontWeight: 600,
      lineHeight: '20px',
      textAlign: 'center',
      letterSpacing: '-1%',
      cursor: 'pointer',
      fontFamily: 'Source Sans Pro',
      [theme.mediaQueries.lg]: {
        fontSize: '22px',
        lineHeight: '30px',
      },
    }),
  chevron: (theme: Theme) =>
    css({
      position: 'absolute',
      right: '24px',
      bottom: '16px',
      height: '32px',
      width: '32px',
      pointerEvents: 'none',
      [theme.mediaQueries.lg]: {
        bottom: '24px',
        height: '48px',
        width: '48px',
      },
    }),
}
